import { FilterConditionOutput } from '@components/ui/AdvancedFilter';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { getInvoicesViewFilterOptions } from '../config/filterOptions';
import { InvoiceStorageTableRow } from '../models/InvoicesView';
import { useInvoice } from './useInvoice';
import { useInvoiceStorages } from './useInvoiceStorages';
import { useInvoicesView } from './useInvoicesView';

const fieldToApiMapping: Record<string, string> = {
  id: 'id',
  code: 'code',
  status: 'status',
  created_at: 'created_at',
  total_amount: 'total_amount',
};

const serializeConditions = (conditions: FilterConditionOutput[]) =>
  JSON.stringify(
    conditions.map(({ column, action, value }) => ({
      column,
      action,
      value,
    })),
  );

const parseFiltersFromParams = (value: unknown): FilterConditionOutput[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value as FilterConditionOutput[];
};

/**
 * useInvoicesViewMain - Hook principal para gestionar toda la vista de InvoicesView
 *
 * Orquesta todos los aspectos de la gestión de prefacturas:
 * - Carga de datos paginados con infinite scroll
 * - Gestión de filtros avanzados y búsqueda (sincronizados con URL)
 * - Sorting de columnas (id, code, created_at, total_amount)
 * - Selección múltiple de prefacturas
 * - Sincronización bidireccional con query params de la URL
 *
 * @returns {Object} Estado completo de la vista con datos, handlers, filtros y configuración
 */
export const useInvoicesViewMain = () => {
  const { t } = useTranslation('invoicesViewModule');
  const currency = useCurrency();

  const { preInvoiceId } = useParams<{ preInvoiceId: string }>();
  const invoiceId = Number(preInvoiceId ?? '0');

  // 🎯 Función para cargar opciones dinámicamente (simplificado para InvoicesView)
  const loadColumnValues = useCallback(async () => {
    // Por ahora no hay filtros dinámicos, pero se puede agregar después
    return [];
  }, []);

  // 🎯 Obtener filterOptions
  const filterOptions = useMemo(() => getInvoicesViewFilterOptions(t, loadColumnValues), [t, loadColumnValues]);

  // 🎯 Query Params (sincronización con URL)
  // ⚠️ NO incluimos selectedColumns en URL - se persisten en localStorage
  const { params: queryParams, updateParams: setQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
    sortBy: { type: 'string', default: 'id' },
    sortDirection: { type: 'string', default: 'desc' },
  });

  // 🎯 Estados locales
  const [searchTerm, setSearchTerm] = useState<string>(queryParams.search as string);
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(
    parseFiltersFromParams(queryParams.filters),
  );
  // 🎯 Las columnas seleccionadas NO se sincronizan con URL, solo con localStorage
  // ColumnSelector maneja la persistencia automáticamente
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  // 🎯 Refs para detectar cambios en filtros
  const previousFiltersRef = useRef<string>('');
  const previousSearchRef = useRef<string>('');

  // 🎯 Sorting state
  const [sorting, setSorting] = useState<{
    field: string;
    direction: 'asc' | 'desc';
  } | null>(() => {
    const sortBy = queryParams.sortBy as string;
    const sortDirection = queryParams.sortDirection as 'asc' | 'desc';
    return sortBy ? { field: sortBy, direction: sortDirection || 'desc' } : null;
  });

  // 🎯 Construir searchFilter para la API
  const searchFilter = useMemo(() => {
    const filters: any[] = [];

    // Agregar condiciones de filtros avanzados
    if (filterConditions && filterConditions.length > 0) {
      filterConditions.forEach((condition) => {
        const apiField = fieldToApiMapping[condition.column] || condition.column;
        const operator = condition.action;
        if (!operator) {
          return;
        }

        // Operadores que no requieren valor
        if (operator === 'is_null' || operator === 'is_not_null') {
          filters.push({
            column: apiField,
            operator,
          });
          return;
        }

        const value = condition.value;

        // Normalizar valores para operadores que requieren arrays
        if (operator === 'in' || operator === 'not_in') {
          if (value === undefined || value === null || value === '') {
            return;
          }

          if (Array.isArray(value)) {
            filters.push({
              column: apiField,
              operator,
              value,
            });
          } else if (typeof value === 'string') {
            const normalized = value
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean);

            if (normalized.length === 0) {
              return;
            }

            filters.push({
              column: apiField,
              operator,
              value: normalized,
            });
          } else {
            filters.push({
              column: apiField,
              operator,
              value: [value],
            });
          }

          return;
        }

        // Evitar enviar valores vacíos
        if (value === undefined || value === null || value === '') {
          return;
        }

        filters.push({
          column: apiField,
          operator,
          value,
        });
      });
    }

    return filters.length > 0
      ? {
          filters,
          logic: 'AND',
        }
      : undefined;
  }, [filterConditions]);

  const normalizedSearch = useMemo(() => {
    const value = searchTerm?.trim();
    return value ? value : undefined;
  }, [searchTerm]);

  // 🎯 Obtener invoice primero para determinar el tipo
  const { invoice } = useInvoice();
  const isStorageType = String(invoice?.type ?? '').toUpperCase() === 'STORAGE';

  // 🎯 Hook de datos con infinite scroll - según el tipo
  const {
    shipments: shipmentsData,
    isLoading: isLoadingShipments,
    isFetching: isFetchingShipments,
    error: errorShipments,
    total: totalShipments,
    refetch: refetchShipments,
    fetchNextPage: fetchNextPageShipments,
    hasNextPage: hasNextPageShipments,
    isFetchingNextPage: isFetchingNextPageShipments,
  } = useInvoicesView({
    invoiceId,
    searchFilter,
    search: normalizedSearch,
    sortBy: sorting?.field || 'id',
    sortDirection: sorting?.direction || 'desc',
    enabled: !isStorageType, // Solo habilitar si NO es storage
  });

  const {
    storages: storagesData,
    isLoading: isLoadingStorages,
    isFetching: isFetchingStorages,
    error: errorStorages,
    total: totalStorages,
    refetch: refetchStorages,
    fetchNextPage: fetchNextPageStorages,
    hasNextPage: hasNextPageStorages,
    isFetchingNextPage: isFetchingNextPageStorages,
  } = useInvoiceStorages({
    invoiceId,
    searchFilter,
    search: normalizedSearch,
    sortBy: sorting?.field || 'product_id',
    sortDirection: sorting?.direction || 'asc',
    enabled: isStorageType, // Solo habilitar si es storage
  });

  // 🎯 Unificar estados según el tipo
  const shipments = isStorageType ? [] : shipmentsData;
  const storages = isStorageType ? storagesData : [];
  const isLoading = isStorageType ? isLoadingStorages : isLoadingShipments;
  const isFetching = isStorageType ? isFetchingStorages : isFetchingShipments;
  const error = isStorageType ? errorStorages : errorShipments;
  const apiTotal = isStorageType ? totalStorages : totalShipments;
  const refetch = isStorageType ? refetchStorages : refetchShipments;
  const fetchNextPage = isStorageType ? fetchNextPageStorages : fetchNextPageShipments;
  const hasNextPage = isStorageType ? hasNextPageStorages : hasNextPageShipments;
  const isFetchingNextPage = isStorageType ? isFetchingNextPageStorages : isFetchingNextPageShipments;

  // 🎯 Detectar si hay filtros activos
  const hasActiveFilters = useMemo(() => {
    const currentFilters = serializeConditions(filterConditions);
    const currentSearch = searchTerm?.trim() || '';

    // Hay filtros activos si hay condiciones o búsqueda

    // Actualizar refs
    previousFiltersRef.current = currentFilters;
    previousSearchRef.current = currentSearch;

    // Hay filtros activos si hay condiciones o búsqueda
    return (filterConditions && filterConditions.length > 0) || (searchTerm && searchTerm.trim() !== '');
  }, [filterConditions, searchTerm]);

  // 🎯 Sincronizar estados locales con query params al montar
  useEffect(() => {
    if (queryParams.search) {
      setSearchTerm(queryParams.search as string);
    }
    if (queryParams.filters) {
      setFilterConditions(parseFiltersFromParams(queryParams.filters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo al montar

  // 🎯 Handlers de filtros
  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      setFilterConditions(conditions);
      setQueryParams({ filters: conditions });
    },
    [setQueryParams],
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearchTerm(value);
      setQueryParams({ search: value });
    },
    [setQueryParams],
  );

  const handleColumnsChange = useCallback((columns: string[]) => {
    // 🎯 Solo actualizar estado local - NO sincronizar con URL
    // ColumnSelector se encarga de persistir en localStorage
    setSelectedColumns(columns);
  }, []);

  // 🎯 Handlers de sorting
  const handleSortingChange = useCallback(
    (newSorting: { field: string; direction: 'asc' | 'desc' } | null) => {
      setSorting(newSorting);
      if (newSorting) {
        setQueryParams({
          sortBy: newSorting.field,
          sortDirection: newSorting.direction,
        });
      }
    },
    [setQueryParams],
  );

  // 🎯 Handlers de selección
  // 🎯 Props para componentes de vista
  const invoicesViewViewProps = useMemo(
    () => ({
      isLoading: isLoading && !isFetching,
      isFetching,
      sorting,
      onSortingChange: handleSortingChange,
    }),
    [isLoading, isFetching, sorting, handleSortingChange],
  );

  // 🎯 Transformar storages en filas de tabla
  const storageTableRows: InvoiceStorageTableRow[] = useMemo(() => {
    const formatVolume = (value: number) => {
      return `${new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      }).format(value)} m³`;
    };

    const formatCurrency = (value?: number | null) => formatCurrencyAmount(value ?? 0, currency);

    const formatDate = (dateString: string) => {
      try {
        return new Date(dateString).toLocaleDateString('es-ES');
      } catch {
        return dateString;
      }
    };

    return storages.map((storage, rowIndex) => {
      const id =
        typeof storage.id === 'number' && Number.isFinite(storage.id)
          ? `storage-${storage.id}`
          : [
              'storage',
              rowIndex,
              storage.product_id,
              storage.team_id,
              storage.period,
              storage.min_storage_date,
              storage.max_storage_date,
              storage.total_volume_m3,
              storage.quantity,
            ].join('-');
      const dateRange =
        storage.min_storage_date === storage.max_storage_date
          ? formatDate(storage.min_storage_date)
          : `${formatDate(storage.min_storage_date)} - ${formatDate(storage.max_storage_date)}`;

      return {
        id,
        productId: storage.product_id,
        product: storage.product?.name ?? '--',
        productCode: storage.product?.code ?? '--',
        team: storage.team?.name ?? '--',
        period: storage.period ?? '--',
        quantity: storage.quantity ?? 0,
        unitVolume: formatVolume(storage.unit_volume_m3),
        totalVolume: formatVolume(storage.total_volume_m3),
        dimensions: `${storage.width_m}m × ${storage.height_m}m × ${storage.length_m}m`,
        linesCount: storage.lines_count ?? 0,
        dateRange,
        baseAmount: formatCurrency(storage.base_amount),
        totalAmount: formatCurrency(storage.total_amount),
      };
    });
  }, [storages, currency]);

  const totalCount = typeof apiTotal === 'number' ? apiTotal : isStorageType ? storages.length : shipments.length;

  return {
    // Estados
    selectedColumns,
    invoiceId,
    /** Mismo objeto que usa GET shipments (export Excel) */
    searchFilter,
    sorting,

    // Datos
    shipments,
    storages,
    storageTableRows,
    isLoading,
    isFetching,
    error,
    total: totalCount,
    isStorageType,

    // Props para componentes
    invoicesViewViewProps,

    // Handlers
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    refetch,

    // Props para infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Handlers de selección
    // Estado de filtros
    hasActiveFilters,
    filterConditions,
    searchTerm,
    filterOptions,
  };
};
